/*  MUI  */
import { Search } from '@mui/icons-material';
import { Box, Button, Menu, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import { FontSizes } from 'application/Shared/Constants';
import { Helpers } from 'application/Shared/Helpers';

/*  NPM  */
import { cloneDeep } from 'lodash';

/*  React  */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/* Internal Components */
import { ManagedTableFilterMenuOption } from './ManagedTableFilterMenuOption';

/*  Constants  */
const { isset } = Helpers;

export const ManagedTableFilterMenu = ({
    disabled,
    filteringHeader,
    filters,
    setFilteringHeader,
    setFilters
}) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const [filterSearch, setFilterSearch] = useState('');
    const [localFilters, setLocalFilters] = useState({});

    useEffect(() => {
        setLocalFilters(cloneDeep(filters));
    }, [filters]);

    if (disabled || !filteringHeader?.menuOpen) {
        return null;
    }

    const handleFilterSearch = (filterSearchValue) => {
        setFilterSearch(filterSearchValue);

        if (localFilters[filteringHeader?.headerOpen?.field]) {
            const showValues = [];
            filteringHeader?.headerOpen?.filterableOptions?.forEach((f) => {
                const { child, name, parentKey, value } = f;

                if (
                    t(name)
                        ?.toLowerCase()
                        .indexOf(filterSearchValue.toLowerCase()) > -1
                ) {
                    showValues.push(value);
                    if (child) {
                        if (!showValues.includes(parentKey)) {
                            showValues.push(parentKey);
                        }
                    }
                }
            });

            localFilters[filteringHeader?.headerOpen?.field].showValues =
                showValues;
        }

        setLocalFilters(localFilters);
    };

    const handleFilterSelect = ({ allSelected, option }) => {
        const filterableOneOption =
            filteringHeader?.headerOpen?.filterableOneOption;
        const { parameter, value, parent, child, children, parentKey } = option;

        if (!Object.keys(filters)?.length) {
            return;
        }

        const currentFilters = cloneDeep(filters);

        // filterableOneOption means only one selected option at a time is allowed
        if (filterableOneOption) {
            // If clicked and exists, then 'unclick' by removing it from the array of filters/currentFilters
            if (currentFilters[parameter].values.includes(value)) {
                currentFilters[parameter].values.splice(
                    currentFilters[parameter].values.indexOf(value),
                    1
                );
            } else {
                currentFilters[parameter].values = [value];
            }

            setFilters(currentFilters);

            return;
        }

        let hasFilter = false;
        let hasValue = false;
        let filter = { parameter, values: [] };
        let values = [];
        if (
            isset(currentFilters, parameter, 'values') &&
            !filterableOneOption
        ) {
            hasFilter = true;
            filter = currentFilters[parameter];
            values = filter.values;
            if (values.indexOf(value) > -1) {
                hasValue = true;
            }
        }

        if (hasFilter) {
            if (hasValue) {
                currentFilters[parameter].values.splice(
                    values.indexOf(value),
                    1
                );
            } else {
                currentFilters[parameter].values.push(value);
            }
        } else {
            currentFilters[parameter] = {
                parameter,
                values: [value]
            };
        }

        const selected = values.includes(value);
        if (parent) {
            const { showValues } = currentFilters[parameter];
            children.forEach((key) => {
                // To toggle the select/unselect all - allSelected is not undefined
                if (typeof allSelected !== 'undefined') {
                    if (
                        allSelected === false &&
                        showValues.includes(key) &&
                        !currentFilters[parameter].values.includes(key)
                    ) {
                        currentFilters[parameter].values.push(key);
                    }
                    if (
                        allSelected === true &&
                        showValues.includes(key) &&
                        currentFilters[parameter].values.includes(key)
                    ) {
                        currentFilters[parameter].values.splice(
                            currentFilters[parameter].values.indexOf(key),
                            1
                        );
                    }
                } else if (selected) {
                    if (
                        !currentFilters[parameter].values.includes(key) &&
                        showValues.includes(key)
                    ) {
                        currentFilters[parameter].values.push(key);
                    }
                } else if (
                    showValues.includes(key) &&
                    currentFilters[parameter].values.includes(key)
                ) {
                    currentFilters[parameter].values.splice(
                        currentFilters[parameter].values.indexOf(key),
                        1
                    );
                }
            });
        }

        if (child) {
            if (
                selected &&
                !currentFilters[parameter].values.includes(parentKey)
            ) {
                currentFilters[parameter].values.push(parentKey);
            }
        }

        setFilters(currentFilters);
    };

    const handleFilterMenuItemOnClick = (option) => {
        handleFilterSelect({ option });
    };

    const handleFilterUnSelectAllOnClick = ({ allSelected, option }) => {
        handleFilterSelect({ allSelected, option });
    };

    const filterMenuHandleResetAll = () => {
        const defaultValues =
            filteringHeader?.headerOpen?.filterableOptions.reduce(
                (acc, option) => {
                    if (option.default === true) {
                        acc.push(option.key);
                    }
                    return acc;
                },
                []
            );

        const newFilters = {
            ...filters,
            [filteringHeader?.headerOpen?.field]: {
                ...filters[filteringHeader?.headerOpen?.field],
                values: defaultValues
            }
        };

        setFilters(newFilters);
    };

    const resetFilterMenu = () => {
        if (filteringHeader?.headerOpen?.searchable) {
            handleFilterSearch('');
        }
        setFilteringHeader({ ...filteringHeader, menuOpen: false });
        setFilterSearch('');
    };

    return (
        <Menu
            anchorEl={document.getElementById(
                `filterList_${filteringHeader?.headerOpen.field}`
            )}
            id={`filterable-columns_${filteringHeader?.headerOpen.field}`}
            onClose={() => resetFilterMenu()}
            open={filteringHeader?.menuOpen}
            slotProps={{
                paper: {
                    'data-testid': `renderFilterMenu_${filteringHeader?.headerOpen.field}`,
                    'data-trackid': `renderFilterMenu_${filteringHeader?.headerOpen.field}`,
                    sx: {
                        boxShadow: theme.shadows[24],
                        minWidth: '300px',
                        padding: '16px'
                    }
                }
            }}
            variant="menu"
        >
            {filteringHeader?.headerOpen?.searchable && (
                <TextField
                    autoComplete="off"
                    fullWidth
                    inputProps={{
                        'data-testid': 'managedTableFilterMenuSearch'
                    }}
                    label={
                        <span>
                            <Search fontSize="small" />
                            <Typography
                                variant="span"
                                style={{
                                    fontSize: FontSizes[0]
                                }}
                            >
                                {t('Common.search')}
                            </Typography>
                        </span>
                    }
                    onChange={(event) => handleFilterSearch(event.target.value)}
                    size="small"
                    value={filterSearch}
                />
            )}
            <Box data-testid="renderFilterMenuOptions">
                {filteringHeader?.headerOpen?.filterableOptions?.map(
                    (option, index) => (
                        <ManagedTableFilterMenuOption
                            filters={localFilters}
                            handleFilterMenuItemOnClick={
                                handleFilterMenuItemOnClick
                            }
                            handleFilterUnSelectAllOnClick={
                                handleFilterUnSelectAllOnClick
                            }
                            index={index}
                            key={`filter_menu_option_filter_menu_item_${option.parameter}_${option.value}`}
                            option={option}
                        />
                    )
                )}
            </Box>
            {/* TODO: SAAS-1213. Remove the conditional below once we refactor both these filters so that resetting works */}
            {filteringHeader?.headerOpen.field !== 'business_owner' &&
                filteringHeader?.headerOpen.field !== 'application.risk' && (
                    <Box>
                        <hr />
                        <Box style={{ float: 'right' }}>
                            <Button
                                data-testid={`resetToDefault_${filteringHeader?.headerOpen.field}`}
                                data-trackid={`table_filterMenu_resetToDefault_${filteringHeader?.headerOpen.field}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    filterMenuHandleResetAll();
                                }}
                                variant="text"
                            >
                                <Typography
                                    style={{ textTransform: 'capitalize' }}
                                >
                                    {t('Common.resetToDefault')}
                                </Typography>
                            </Button>
                        </Box>
                    </Box>
                )}
        </Menu>
    );
};
